import React, { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
type SelectProps = {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
};
const menuShadow = 'shadow-[0_0_0_1px_rgba(70,28,64,0.08),0_2px_4px_rgba(70,28,64,0.04),0_16px_36px_-12px_rgba(70,28,64,0.18)]';
export function Select({
  label,
  value,
  options,
  onChange
}: SelectProps) {
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(() => Math.max(0, options.indexOf(value)));
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const openMenu = () => {
    setHighlight(Math.max(0, options.indexOf(value)));
    setOpen(true);
  };
  const choose = (i: number) => {
    onChange(options[i]);
    setOpen(false);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') return setOpen(false);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return openMenu();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight(h => (h + step + options.length) % options.length);
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (open) choose(highlight);else openMenu();
    }
    if (e.key === 'Tab') setOpen(false);
  };
  return <div ref={rootRef} className="relative" data-id="element-499" data-mp="1i43dm8ptk7hz">
      <button type="button" id={`${id}-button`} aria-haspopup="listbox" aria-expanded={open} aria-controls={`${id}-list`} aria-activedescendant={open ? `${id}-opt-${highlight}` : undefined} onClick={() => open ? setOpen(false) : openMenu()} onKeyDown={onKeyDown} className={`flex h-14 w-full items-center gap-3 rounded-[8px] bg-canvas px-4 text-left transition-shadow duration-150 focus:outline-none ${open ? 'shadow-[inset_0_0_0_1px_rgba(13,13,18,0.5)]' : 'shadow-[inset_0_0_0_1px_rgba(70,28,64,0.06)] focus-visible:shadow-[inset_0_0_0_1px_rgba(13,13,18,0.5)]'}`} data-id="element-500" data-mp="1i4v63ao4dp41">
        <span className="min-w-0 flex-1" data-id="element-501" data-mp="3tshznj7te80d">
          <span className="block text-[11px] leading-tight text-muted" data-id="element-502" data-mp="3or0w1fxy47g3">{label}</span>
          <span className="block truncate text-[15px] leading-tight text-ink" data-id="element-503" data-mp="3k0c2zw6mb2eu">{value}</span>
        </span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 text-muted transition-transform duration-150 ease-out ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-504" data-mp="3fzik5h818dst" />
      </button>

      <AnimatePresence data-id="element-505" data-mp="bk90s5y9y5xw">
        {open && <motion.ul id={`${id}-list`} role="listbox" aria-labelledby={`${id}-button`} initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.16,
        ease: [0.23, 1, 0.32, 1]
      }} style={{
        transformOrigin: 'top'
      }} className={`absolute left-0 right-0 top-[calc(100%+6px)] z-30 max-h-[280px] overflow-auto rounded-[10px] bg-white p-1.5 ${menuShadow}`} data-id="element-506" data-mp="bjhejlqkhkka">
            {options.map((option, i) => {
          const selected = option === value;
          return <li key={option} id={`${id}-opt-${i}`} role="option" aria-selected={selected} onMouseEnter={() => setHighlight(i)} onMouseDown={e => e.preventDefault()} onClick={() => choose(i)} className={`flex cursor-pointer items-center justify-between gap-3 rounded-[6px] px-3 py-2.5 text-[15px] transition-colors duration-100 ${i === highlight ? 'bg-canvas' : ''} ${selected ? 'font-medium text-ink' : 'text-ink/80'}`} data-id="element-507" data-mp="2mxhg7izm425g" data-mpi={i}>
                  {option}
                  {selected && <CheckIcon className="h-4 w-4 shrink-0 text-ink" strokeWidth={2.25} data-id="element-508" data-mp="2rzfagcr2sfrf" />}
                </li>;
        })}
          </motion.ul>}
      </AnimatePresence>
    </div>;
}